import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import * as locales from '@mui/material/locale';
import { MEMO_PAGE_LIST_PANEL, MEMO_PAGE_VIEW_PANEL } from '../common/MetaData';
import { MessageShape } from '../model/AppUIShapes';
import { DEFAULT_LANGUAGE, MUI_LANGUAGE_MAP, AppLanguage, MuiLanguage } from '../common/MetaSetting';
import getNewGUIDString from '../util/GUID';
import { fetchUserMarksByIds } from '../hooks/api/userAPI';

type SupportedLocales = keyof typeof locales;

/** 无效缓存条目的过期时间：1小时（毫秒） */
const INVALID_CACHE_TTL_MS = 60 * 60 * 1000;

/** 用户缓存条目 */
interface CachedUserInfo {
  nickName: string;
  avatarPath: string;
  username: string;
  /** 加入缓存的时间（epoch 毫秒） */
  cachedAt: number;
  /** 是否为有效用户（false 表示查询过但未找到） */
  isValid: boolean;
}

/** 群组缓存条目 */
interface CachedGroupInfo {
  groupName: string;
  avatarPath: string;
  members: CachedGroupMember[];
  /** 加入缓存的时间（epoch 毫秒） */
  cachedAt: number;
  /** 是否为有效群组（false 表示查询过但未找到） */
  isValid: boolean;
}

interface CachedGroupMember {
  userId: string;
}

interface AppState {
  // State from appSlice
  currentLanguage: AppLanguage;
  currentMuiLanguage: MuiLanguage;
  currentListPanel: number;
  currentViewPanel: number;
  messages: MessageShape[];
  status: 'idle' | 'loading' | 'failed';
  isSyncing: boolean;

  // Messages data
  friends: any[];
  userGroups: any[];
  pendingRequests: any[];
  groupJoinRequests: any[];

  // 用户信息缓存
  userInfoMap: Record<string, CachedUserInfo>;
  // 群组信息缓存
  groupInfoMap: Record<string, CachedGroupInfo>;
  // 正在获取中的 userId 集合（防止重复请求）
  _fetchingUserIds: Set<string>;

  // Actions from appSliceExisting state
  isSidebarOpen: boolean;
  currentView: string;
  theme: 'light' | 'dark';
  isLoading: boolean;
  error: string | null;

  // Actions from appSlice
  changeCurrentListPanel: (panel: number) => void;
  changeCurrentViewPanel: (panel: number) => void;
  changeCurrentLanguage: (language: AppLanguage) => void;
  appendMessageToLocal: (message: MessageShape) => void;
  removeMessageFromLocal: (guid: string) => void;
  switchToEditView: () => void;
  switchToShareView: () => void;
  addMessage: (messageType: number, message: string) => void;
  setCurrentMuiLanguage: (language: MuiLanguage) => void;
  setIsLoading: (loading: boolean) => void;

  // Messages data actions
  setFriends: (friends: any[]) => void;
  setUserGroups: (userGroups: any[]) => void;
  setPendingRequests: (pendingRequests: any[]) => void;
  setGroupJoinRequests: (groupJoinRequests: any[]) => void;

  // 缓存 actions
  updateUserInfoCache: (users: { userId: string; nickName?: string; avatarPath?: string; username?: string }[]) => void;
  updateGroupInfoCache: (groupId: string, info: { groupName?: string; avatarPath?: string; members?: { userId: string }[] }) => void;
  getUserInfo: (userId: string) => CachedUserInfo | null;
  getGroupMemberInfo: (groupId: string, userId: string) => { userId: string } | null;
  /**
   * 确保用户信息在缓存中。如果不在缓存或无效且超过1小时，触发从服务器获取。
   * 返回当前缓存中的信息（可能为 null），获取完成后会异步更新缓存。
   */
  ensureUserInfo: (userId: string) => CachedUserInfo | null;
  /** 批量确保用户信息在缓存中 */
  ensureUserInfos: (userIds: string[]) => void;

  // Existing actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setCurrentView: (view: string) => void;
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: AppLanguage) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

/**
 * 用缓存数据回填列表数据对象，使其包含 nickName/avatarPath 等详细信息。
 */
function enrichListItemsFromCache(
  get: () => AppState,
  set: (partial: Partial<AppState> | ((state: AppState) => Partial<AppState>)) => void,
  userInfoMap: Record<string, CachedUserInfo>,
  groupInfoMap: Record<string, CachedGroupInfo>
) {
  const state = get();

  const enrichedFriends = state.friends.map(f => {
    const uid = String(f.friendId ?? f.userId ?? '');
    const cached = userInfoMap[uid];
    if (cached?.isValid) {
      return {
        ...f,
      };
    }
    return f;
  });

  const enrichedUserGroups = state.userGroups.map(g => {
    return g;
  });

  const enrichedPendingRequests = state.pendingRequests.map(r => {
    const uid = String(r.friendId ?? r.userId ?? '');
    const userCached = userInfoMap[uid];
    let updated = { ...r };
    if (userCached?.nickName) {
      updated.nickName = userCached.nickName;
    }
    if (userCached?.avatarPath) {
      updated.avatarPath = userCached.avatarPath;
    }
    return updated;
  });

  const enrichedGroupJoinRequests = state.groupJoinRequests.map(r => {
    const gid = String(r.groupId ?? '');
    const groupCached = groupInfoMap[gid];
    let updated = { ...r };
    if (groupCached?.isValid && groupCached.groupName) {
      updated.groupName = groupCached.groupName;
    }
    return updated;
  });

  set({
    friends: enrichedFriends,
    userGroups: enrichedUserGroups,
    pendingRequests: enrichedPendingRequests,
    groupJoinRequests: enrichedGroupJoinRequests,
  });
}

/**
 * 公共方法：通过 userId 列表异步获取用户详细信息，更新 userInfoMap 和 groupInfoMap 缓存，
 * 并回填列表数据对象。
 * 用于 setFriends/setUserGroups/setPendingRequests 等场景。
 */
function fetchAndFillUserDetails(
  userIds: number[],
  get: () => AppState,
  set: (partial: Partial<AppState> | ((state: AppState) => Partial<AppState>)) => void
) {
  fetchUserMarksByIds(userIds).then((results) => {
    const marks = Array.isArray(results) ? results : (results?.data || []);
    if (marks.length > 0) {
      const updatedMap = { ...get().userInfoMap };
      const updatedGroupMap = { ...get().groupInfoMap };
      const fetchNow = Date.now();

      for (const mark of marks) {
        // 更新 userInfoMap：通过 userId 直接匹配
        const uid = String(mark.userId);
        const entry = updatedMap[uid];
        if (entry) {
          updatedMap[uid] = {
            ...entry,
            nickName: mark.nickName || entry.nickName,
            avatarPath: mark.avatarPath || entry.avatarPath,
            username: mark.username || entry.username,
            cachedAt: fetchNow,
          };
        } else {
          updatedMap[uid] = {
            nickName: mark.nickName || '',
            avatarPath: mark.avatarPath || '',
            username: mark.username || '',
            cachedAt: fetchNow,
            isValid: true,
          };
        }
        // 同步更新 groupInfoMap 中成员信息（nickName/avatarPath 已移至 userInfoMap）
        for (const group of Object.values(updatedGroupMap)) {
          const member = group.members.find(m => String(m.userId) === uid);
          if (member) {
            // CachedGroupMember 不再存储 nickName/avatarPath，无需更新
          }
        }
      }

      set({ userInfoMap: updatedMap, groupInfoMap: updatedGroupMap });
      // 回填列表数据对象
      enrichListItemsFromCache(get, set, updatedMap, updatedGroupMap);
    }
  }).catch((err) => {
    console.error('fetchAndFillUserDetails failed:', err);
  });
}

export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // Initial state from appSlice
      currentLanguage: DEFAULT_LANGUAGE,
      currentMuiLanguage: MUI_LANGUAGE_MAP[DEFAULT_LANGUAGE],
      currentListPanel: MEMO_PAGE_LIST_PANEL.SECTION,
      currentViewPanel: MEMO_PAGE_VIEW_PANEL.EDIT,
      messages: [],
      status: 'idle',
      isSyncing: false,

      // Messages data initial state
      friends: [],
      userGroups: [],
      pendingRequests: [],
      groupJoinRequests: [],
      userInfoMap: {},
      groupInfoMap: {},
      _fetchingUserIds: new Set<string>(),

      // Existing initial state
      isSidebarOpen: true,
      currentView: 'memo',
      theme: 'light',
      isLoading: false,
      error: null,

      // Actions from appSlice
      changeCurrentListPanel: (panel) => set({ currentListPanel: panel }),
      changeCurrentViewPanel: (panel) => set({ currentViewPanel: panel }),
      changeCurrentLanguage: (language: AppLanguage) =>
        set({
          currentLanguage: language,
          currentMuiLanguage: MUI_LANGUAGE_MAP[language],
        }),
      appendMessageToLocal: (message) =>
        set((state) => ({
          messages: [...state.messages, message]
        })),
      removeMessageFromLocal: (guid) =>
        set((state) => ({
          messages: state.messages.filter((m) => m.guid !== guid)
        })),
      switchToEditView: () => set({ currentViewPanel: MEMO_PAGE_VIEW_PANEL.EDIT }),
      switchToShareView: () => set({ currentViewPanel: MEMO_PAGE_VIEW_PANEL.SHARE }),

      addMessage: (messageType, message) => {
        const guid = getNewGUIDString();
        const newMessage: MessageShape = {
          guid,
          messageType,
          message
        };
        set((state) => ({
          messages: [...state.messages, newMessage]
        }));
      },
      setCurrentMuiLanguage: (language: MuiLanguage) => set({ currentMuiLanguage: language }),
      setIsLoading: (loading: boolean) => set({ isLoading: loading }),

      // Messages data actions
      setFriends: (friends: any[]) => {
        const newUserInfoMap = { ...get().userInfoMap };
        const now = Date.now();
        const userIdsToFetch: number[] = [];

        // 用已有缓存回填 friends 数据对象
        const enrichedFriends = friends.map(f => {
          const uid = String(f.friendId ?? f.userId ?? '');
          const userId = f.friendId ?? f.userId;
          if (uid) {
            const cached = newUserInfoMap[uid];
            const nickName = (cached?.isValid && cached.nickName) ? cached.nickName : '';
            const avatarPath = (cached?.isValid && cached.avatarPath) ? cached.avatarPath : '';

            if (!cached || !cached.isValid) {
              newUserInfoMap[uid] = { nickName, avatarPath, username: '', cachedAt: now, isValid: true };
            } else {
              newUserInfoMap[uid] = { ...cached, cachedAt: now };
            }

            if ((!nickName || !avatarPath) && userId) {
              userIdsToFetch.push(Number(userId));
            }
          }
          return f;
        });

        set({ friends: enrichedFriends, userInfoMap: newUserInfoMap });

        if (userIdsToFetch.length > 0) {
          fetchAndFillUserDetails(Array.from(new Set(userIdsToFetch)), get, set);
        }
      },
      setUserGroups: (userGroups: any[]) => {
        const newGroupInfoMap = { ...get().groupInfoMap };
        const newUserInfoMap = { ...get().userInfoMap };
        const now = Date.now();
        const userIdsToFetch: number[] = [];

        // 用已有缓存回填 userGroups 数据对象的成员详细信息
        const enrichedUserGroups = userGroups.map(g => {
          const gid = String(g.groupId ?? g.id ?? '');
          if (gid) {
            const existingGroup = newGroupInfoMap[gid];
            const members: CachedGroupMember[] = g.members?.map((m: any) => {
              const mid = String(m.userId ?? '');
              const userId = m.userId;
              const cached = mid ? newUserInfoMap[mid] : null;
              const nickName = (cached?.isValid && cached.nickName) ? cached.nickName : '';
              const avatarPath = (cached?.isValid && cached.avatarPath) ? cached.avatarPath : '';

              if (mid && (!newUserInfoMap[mid] || !newUserInfoMap[mid].isValid)) {
                newUserInfoMap[mid] = { nickName, avatarPath, username: '', cachedAt: now, isValid: true };
              } else if (mid && newUserInfoMap[mid]) {
                newUserInfoMap[mid] = { ...newUserInfoMap[mid], cachedAt: now };
              }

              if (mid && (!nickName || !avatarPath) && userId) {
                userIdsToFetch.push(Number(userId));
              }

              return { userId: mid };
            }) || existingGroup?.members || [];

            newGroupInfoMap[gid] = {
              groupName: g.groupName || g.name || existingGroup?.groupName || '',
              avatarPath: g.avatarPath || g.groupAvatarPath || existingGroup?.avatarPath || '',
              members,
              cachedAt: now,
              isValid: true,
            };

            return g;
          }
          return g;
        });

        set({ userGroups: enrichedUserGroups, groupInfoMap: newGroupInfoMap, userInfoMap: newUserInfoMap });

        if (userIdsToFetch.length > 0) {
          fetchAndFillUserDetails(Array.from(new Set(userIdsToFetch)), get, set);
        }
      },
      setPendingRequests: (pendingRequests: any[]) => {
        const newUserInfoMap = { ...get().userInfoMap };
        const now = Date.now();
        const userIdsToFetch: number[] = [];

        // 用已有缓存回填 pendingRequests 数据对象
        const enrichedRequests = pendingRequests.map(r => {
          const uid = String(r.friendId ?? r.userId ?? '');
          const userId = r.friendId ?? r.userId;
          if (uid) {
            const cached = newUserInfoMap[uid];
            const nickName = (cached?.isValid && cached.nickName) ? cached.nickName : '';
            const avatarPath = (cached?.isValid && cached.avatarPath) ? cached.avatarPath : '';

            if (!cached || !cached.isValid) {
              newUserInfoMap[uid] = { nickName, avatarPath, username: '', cachedAt: now, isValid: true };
            } else {
              newUserInfoMap[uid] = { ...cached, cachedAt: now };
            }

            if ((!nickName || !avatarPath) && userId) {
              userIdsToFetch.push(Number(userId));
            }
          }
          return r;
        });

        set({ pendingRequests: enrichedRequests, userInfoMap: newUserInfoMap });

        if (userIdsToFetch.length > 0) {
          fetchAndFillUserDetails(Array.from(new Set(userIdsToFetch)), get, set);
        }
      },
      setGroupJoinRequests: (groupJoinRequests: any[]) => {
        const newUserInfoMap = { ...get().userInfoMap };
        const newGroupInfoMap = { ...get().groupInfoMap };
        const now = Date.now();
        const userIdsToFetch: number[] = [];

        // 用已有缓存回填 groupJoinRequests 数据对象
        const enrichedRequests = groupJoinRequests.map(r => {
          const uid = String(r.applicantUserId ?? r.applicantId ?? '');
          const userId = r.applicantUserId ?? r.applicantId;
          const gid = String(r.groupId ?? '');
          let updated = { ...r };

          if (uid) {
            const cached = newUserInfoMap[uid];
            const nickName = (cached?.isValid && cached.nickName) ? cached.nickName : '';
            const avatarPath = (cached?.isValid && cached.avatarPath) ? cached.avatarPath : '';

            if (!cached || !cached.isValid) {
              newUserInfoMap[uid] = { nickName, avatarPath, username: '', cachedAt: now, isValid: true };
            } else {
              newUserInfoMap[uid] = { ...cached, cachedAt: now };
            }

            if ((!nickName || !avatarPath) && userId) {
              userIdsToFetch.push(Number(userId));
            }
          }

          // 从 groupInfoMap 缓存回填 groupName
          if (gid) {
            const groupCached = newGroupInfoMap[gid];
            if (groupCached?.isValid && groupCached.groupName) {
              updated.groupName = groupCached.groupName;
            }
          }

          return updated;
        });

        set({ groupJoinRequests: enrichedRequests, userInfoMap: newUserInfoMap, groupInfoMap: newGroupInfoMap });

        if (userIdsToFetch.length > 0) {
          fetchAndFillUserDetails(Array.from(new Set(userIdsToFetch)), get, set);
        }
      },

      // 缓存 actions
      updateUserInfoCache: (users) => {
        const newMap = { ...get().userInfoMap };
        const now = Date.now();
        for (const u of users) {
          const uid = String(u.userId);
          if (uid) {
            newMap[uid] = {
              nickName: u.nickName || '',
              avatarPath: u.avatarPath || '',
              username: u.username || '',
              cachedAt: now,
              isValid: true,
            };
          }
        }
        set({ userInfoMap: newMap });
      },
      updateGroupInfoCache: (groupId, info) => {
        const newMap = { ...get().groupInfoMap };
        const gid = String(groupId);
        const existing = newMap[gid] || { groupName: '', avatarPath: '', members: [], cachedAt: 0, isValid: false };
        const now = Date.now();
        newMap[gid] = {
          groupName: info.groupName || existing.groupName,
          avatarPath: info.avatarPath || existing.avatarPath,
          members: info.members?.map((m) => ({
            userId: String(m.userId),
          })) || existing.members,
          cachedAt: now,
          isValid: true,
        };
        set({ groupInfoMap: newMap });
      },
      getUserInfo: (userId) => {
        const entry = get().userInfoMap[String(userId)];
        if (!entry) return null;
        if (!entry.isValid) return null;
        return entry;
      },
      getGroupMemberInfo: (groupId, userId) => {
        const group = get().groupInfoMap[String(groupId)];
        if (!group || !group.isValid) return null;
        const member = group.members.find((m) => m.userId === String(userId));
        return member || null;
      },

      ensureUserInfo: (userId: string) => {
        const uid = String(userId);
        const entry = get().userInfoMap[uid];
        const now = Date.now();

        // 1. 有效缓存且信息完整 → 直接返回
        if (entry && entry.isValid && entry.nickName && entry.avatarPath) {
          if (now - entry.cachedAt > 60000) {
            set({ userInfoMap: { ...get().userInfoMap, [uid]: { ...entry, cachedAt: now } } });
          }
          return entry;
        }

        // 2. 无效缓存但未过期 → 返回 null
        if (entry && !entry.isValid && (now - entry.cachedAt < INVALID_CACHE_TTL_MS)) {
          return null;
        }

        // 3. 用 userId 异步获取
        const numericId = Number(uid);
        if (!numericId) return entry || null;

        const fetchingIds = get()._fetchingUserIds;
        if (fetchingIds.has(uid)) {
          return null;
        }

        const newFetchingIds = new Set(fetchingIds);
        newFetchingIds.add(uid);
        set({ _fetchingUserIds: newFetchingIds });

        fetchUserMarksByIds([numericId]).then((results) => {
          const marks = Array.isArray(results) ? results : (results?.data || []);
          const newMap = { ...get().userInfoMap };
          const fetchNow = Date.now();

          if (marks.length > 0) {
            const mark = marks[0];
            newMap[uid] = {
              nickName: mark.nickName || entry?.nickName || '',
              avatarPath: mark.avatarPath || entry?.avatarPath || '',
              username: mark.username || entry?.username || '',
              cachedAt: fetchNow,
              isValid: true,
            };
          } else {
            newMap[uid] = {
              nickName: entry?.nickName || '',
              avatarPath: entry?.avatarPath || '',
              username: entry?.username || '',
              cachedAt: fetchNow,
              isValid: false,
            };
          }

          const updatedFetchingIds = new Set(get()._fetchingUserIds);
          updatedFetchingIds.delete(uid);
          set({ userInfoMap: newMap, _fetchingUserIds: updatedFetchingIds });
        }).catch(() => {
          const newMap = { ...get().userInfoMap };
          newMap[uid] = {
            nickName: entry?.nickName || '',
            avatarPath: entry?.avatarPath || '',
            username: entry?.username || '',
            cachedAt: Date.now(),
            isValid: false,
          };
          const updatedFetchingIds = new Set(get()._fetchingUserIds);
          updatedFetchingIds.delete(uid);
          set({ userInfoMap: newMap, _fetchingUserIds: updatedFetchingIds });
        });

        return entry || null;
      },

      ensureUserInfos: (userIds: string[]) => {
        const now = Date.now();
        const needFetch: string[] = [];

        for (const uid of userIds) {
          const id = String(uid);
          const entry = get().userInfoMap[id];

          if (entry && entry.isValid && entry.nickName && entry.avatarPath) {
            if (now - entry.cachedAt > 60000) {
              set({ userInfoMap: { ...get().userInfoMap, [id]: { ...entry, cachedAt: now } } });
            }
          } else if (entry && !entry.isValid && (now - entry.cachedAt < INVALID_CACHE_TTL_MS)) {
            // 无效但未过期，跳过
          } else {
            const fetchingIds = get()._fetchingUserIds;
            if (!fetchingIds.has(id) && Number(id)) {
              needFetch.push(id);
            }
          }
        }

        if (needFetch.length === 0) return;

        // 标记为正在获取
        const newFetchingIds = new Set(get()._fetchingUserIds);
        for (const id of needFetch) {
          newFetchingIds.add(id);
        }
        set({ _fetchingUserIds: newFetchingIds });

        const numericIds = needFetch.map(id => Number(id));
        fetchUserMarksByIds(numericIds).then((results) => {
          const marks = Array.isArray(results) ? results : (results?.data || []);
          const newMap = { ...get().userInfoMap };
          const fetchNow = Date.now();
          const returnedUids = new Set<string>();

          if (marks.length > 0) {
            for (const mark of marks) {
              const uid = String(mark.userId);
              if (uid) {
                const existing = newMap[uid];
                returnedUids.add(uid);
                newMap[uid] = {
                  nickName: mark.nickName || existing?.nickName || '',
                  avatarPath: mark.avatarPath || existing?.avatarPath || '',
                  username: mark.username || existing?.username || '',
                  cachedAt: fetchNow,
                  isValid: true,
                };
              }
            }
          }

          for (const uid of needFetch) {
            if (!returnedUids.has(uid)) {
              const existing = newMap[uid];
              newMap[uid] = {
                nickName: existing?.nickName || '',
                avatarPath: existing?.avatarPath || '',
                username: existing?.username || '',
                cachedAt: fetchNow,
                isValid: false,
              };
            }
          }

          const updatedFetchingIds = new Set(get()._fetchingUserIds);
          for (const id of needFetch) {
            updatedFetchingIds.delete(id);
          }
          set({ userInfoMap: newMap, _fetchingUserIds: updatedFetchingIds });
        }).catch(() => {
          const newMap = { ...get().userInfoMap };
          const fetchNow = Date.now();
          for (const uid of needFetch) {
            const existing = newMap[uid];
            newMap[uid] = {
              nickName: existing?.nickName || '',
              avatarPath: existing?.avatarPath || '',
              username: existing?.username || '',
              cachedAt: fetchNow,
              isValid: false,
            };
          }
          const updatedFetchingIds = new Set(get()._fetchingUserIds);
          for (const id of needFetch) {
            updatedFetchingIds.delete(id);
          }
          set({ userInfoMap: newMap, _fetchingUserIds: updatedFetchingIds });
        });
      },

      // Existing actions
      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
      setSidebarOpen: (open) => set({ isSidebarOpen: open }),
      setCurrentView: (view) => set({ currentView: view }),
      toggleTheme: () => set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      setTheme: (theme) => set({ theme }),
      setLanguage: (language: AppLanguage) => set({ currentLanguage: language }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null })
    }),
    {
      name: 'app-store'
    }
  )
);
